import React, { useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api from "@/services/api";
import { Loader2, TrendingUp, Trophy, Target, Calendar } from "lucide-react";

const fmt = (n) => `$${Math.round(n || 0).toLocaleString("es-CO")}`;

const proximoPago = () => {
  const hoy = new Date();
  const dia = hoy.getDay();
  const faltan = dia === 0 ? 1 : dia === 1 ? 0 : 8 - dia;
  const lunes = new Date(hoy);
  lunes.setDate(hoy.getDate() + faltan);
  return lunes.toLocaleDateString("es-CO", { weekday: "long", day: "numeric", month: "long" });
};

export default function VendedorDesempeno() {
  const [data, setData] = useState(null);
  const [pagos, setPagos] = useState([]);

  useEffect(() => {
    api.get("/incentivos/mi-desempeno")
      .then((res) => setData(res.data))
      .catch(() => toast.error("Error cargando tu desempeño"));
    api.get("/nomina/historial")
      .then((res) => setPagos(res.data || []))
      .catch(() => {});
  }, []);

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-torta-brown" /></div>;

  const { hoy, semana, mes } = data;
  const prox = hoy.proximo_nivel;
  const progreso = prox ? Math.min(100, (hoy.venta_neta / prox.desde) * 100) : 100;

  return (
    <div className="space-y-4 max-w-2xl mx-auto" data-testid="vendedor-desempeno-page">
      <h1 className="text-xl font-bold text-torta-dark flex items-center gap-2"><Trophy className="w-5 h-5 text-torta-brown" /> Mis Ganancias</h1>

      {/* HOY */}
      <div className="bg-gradient-to-br from-[#4A2C0A] to-[#7a4a15] text-white rounded-2xl p-5">
        <p className="text-xs uppercase text-amber-200 mb-1">Hoy {hoy.nivel_diario && `· Nivel ${hoy.nivel_diario}`}</p>
        <p className="text-3xl font-bold" data-testid="ganancia-hoy">{fmt(hoy.total)}</p>
        <p className="text-xs text-amber-100 mt-1">ganancia estimada del día</p>
        <div className="grid grid-cols-3 gap-2 mt-4 text-center">
          <div><p className="font-bold">{fmt(hoy.venta_neta)}</p><p className="text-[10px] text-amber-200">Ventas</p></div>
          <div><p className="font-bold">{fmt(hoy.comision_sku)}</p><p className="text-[10px] text-amber-200">Comisiones</p></div>
          <div><p className="font-bold">{fmt(hoy.bono_diario + hoy.bono_mix + hoy.incentivos_extra)}</p><p className="text-[10px] text-amber-200">Bonos</p></div>
        </div>
      </div>

      {/* Progreso al próximo nivel */}
      {prox && (
        <div className="bg-white rounded-2xl border p-4" data-testid="progreso-nivel">
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-4 h-4 text-torta-brown" />
            <p className="text-sm font-semibold text-torta-dark">Próxima meta: Nivel {prox.nombre}</p>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
            <div className="bg-gradient-to-r from-amber-400 to-[#C8902A] h-3 rounded-full transition-all" style={{ width: `${progreso}%` }} />
          </div>
          <p className="text-xs text-gray-600 mt-2">{fmt(hoy.venta_neta)} / {fmt(prox.desde)}</p>
          <p className="text-sm text-torta-brown font-medium mt-1">
            Te faltan {fmt(prox.falta)} en ventas para alcanzar Nivel {prox.nombre} y ganar un bono de {fmt(prox.bono)} 💪
          </p>
        </div>
      )}

      {/* Próximo pago */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-2xl p-4 flex items-center justify-between" data-testid="proximo-pago-card">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center"><Calendar className="w-5 h-5" /></div>
          <div>
            <p className="text-xs text-white/80">Próximo pago</p>
            <p className="font-semibold capitalize">{proximoPago()}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-xs text-white/80">Semana (estimado)</p>
          <p className="text-xl font-bold">{fmt(semana.total)}</p>
        </div>
      </div>

      {/* Semana y Mes */}
      <div className="grid grid-cols-2 gap-3">
        <PeriodoCard titulo="Esta semana" data={semana} extra={semana.nivel_semanal && `Bono semanal ${semana.nivel_semanal}: ${fmt(semana.bono_semanal)}`} />
        <PeriodoCard titulo="Este mes" data={mes} extra={mes.nivel_mensual && `Bono mensual: ${fmt(mes.bono_mensual)}`} />
      </div>

      {/* Historial de días */}
      {data.historial_dias?.length > 0 && (
        <div className="bg-white rounded-2xl border p-4">
          <p className="text-sm font-semibold text-torta-dark mb-2 flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Días del mes</p>
          <div className="space-y-1">
            {data.historial_dias.map((d) => (
              <div key={d.fecha} className="flex justify-between items-center text-sm border-b last:border-0 py-1.5">
                <span className="text-gray-600">{d.fecha}</span>
                <span className="text-xs">{d.nivel_diario && <Badge className="bg-amber-100 text-amber-800 border-0 mr-2">{d.nivel_diario}</Badge>}{fmt(d.venta_neta)}</span>
                <span className="font-semibold text-green-700">{fmt(d.total)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Liquidaciones */}
      {data.liquidaciones?.length > 0 && (
        <div className="bg-white rounded-2xl border p-4">
          <p className="text-sm font-semibold text-torta-dark mb-2">Mis Liquidaciones</p>
          {data.liquidaciones.map((l) => (
            <div key={l.id} className="flex justify-between items-center text-sm py-1.5 border-b last:border-0">
              <span className="text-gray-600">{l.desde} → {l.hasta}</span>
              <Badge className={`border-0 capitalize ${l.estado === "pagada" ? "bg-green-100 text-green-800" : l.estado === "aprobada" ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-700"}`}>{l.estado}</Badge>
              <span className="font-bold text-green-700">{fmt(l.total)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Historial de pagos (nómina) */}
      {pagos.length > 0 && (
        <div className="bg-white rounded-2xl border p-4" data-testid="historial-pagos">
          <p className="text-sm font-semibold text-torta-dark mb-2 flex items-center gap-2"><Calendar className="w-4 h-4" /> Historial de Pagos</p>
          {pagos.map((p, i) => (
            <div key={i} className="flex justify-between items-center text-sm py-1.5 border-b last:border-0">
              <span className="text-gray-600">Semana del {new Date(p.fecha_inicio).toLocaleDateString("es-CO")}</span>
              <Badge className={`border-0 ${p.estado === "pagado" ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-700"}`}>{p.estado === "pagado" ? "Pagado" : "Pendiente"}</Badge>
              <span className="font-bold text-green-700">{fmt(p.total)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const PeriodoCard = ({ titulo, data, extra }) => (
  <div className="bg-white rounded-2xl border p-4">
    <p className="text-xs text-gray-500 uppercase">{titulo}</p>
    <p className="text-xl font-bold text-torta-dark">{fmt(data.total)}</p>
    <p className="text-xs text-gray-500">Ventas: {fmt(data.venta_neta)} · {data.dias_activos} días activos</p>
    {extra && <p className="text-xs text-green-700 mt-1">{extra}</p>}
  </div>
);
